/* eslint-disable max-lines */
/**
 * Main Token Examiner Component
 * Comprehensive relationship intelligence platform
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Button,
  Alert,
  Snackbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Hub as HubIcon,
  TableChart as MatrixIcon,
  Timeline as TimelineIcon,
  List as ListIcon,
  ViewColumn as SplitViewIcon,
  ViewComfy as ViewColumnIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  NetworkCheck as NetworkCheckIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useEntityContext } from '../EntityContextSwitch';
import api from '../../services/api';
import useTokenExaminerStore from './stores/tokenExaminerStore';
import { getFaanaHealthColor, getHealthCategory } from './utils/faanaScoring';
import { getContrastText } from './utils/colorContrast';

// Import visualization components
import ForceDirectedNetwork from './visualizations/ForceDirectedNetwork';
import RelationshipMatrix from './visualizations/RelationshipMatrix';
import TemporalEvolution from './visualizations/TemporalEvolution';
import RelationshipList from './visualizations/RelationshipList';
import FilterPanel from './components/FilterPanel';
import ExaminationProgress from './components/ExaminationProgress';
import NetworkInsightsPanel from './components/NetworkInsightsPanel';
import BatchActionsBar from './components/BatchActionsBar';
import NetworkGraphView from './networkAnalysis/NetworkGraphView';

// View mode configurations
const VIEW_MODES = {
  sna: {
    label: 'SNA Network (New)',
    icon: <NetworkCheckIcon />,
    description: 'Social Network Analysis from 3-pass TE data (relationship rollups)',
    component: NetworkGraphView,
  },
  network: {
    label: 'Network View (Legacy)',
    icon: <HubIcon />,
    description: 'Interactive force-directed relationship graph (legacy discoveredRelationships)',
    component: ForceDirectedNetwork,
  },
  matrix: {
    label: 'Matrix View',
    icon: <MatrixIcon />,
    description: 'Relationship health matrix for bulk operations',
    component: RelationshipMatrix,
  },
  temporal: {
    label: 'Temporal View',
    icon: <TimelineIcon />,
    description: 'Relationship evolution over time',
    component: TemporalEvolution,
  },
  list: {
    label: 'List View',
    icon: <ListIcon />,
    description: 'Traditional table view with sorting',
    component: RelationshipList,
  },
  split: {
    label: 'Split View',
    icon: <SplitViewIcon />,
    description: 'Graph and list side by side',
    component: null, // Special handling for split view
  },
};

// eslint-disable-next-line max-lines-per-function
function TokenExaminer() {
  const { user } = useAuth();
  const { currentEntity } = useEntityContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [selectedRelationshipIds, setSelectedRelationshipIds] = useState([]);
  const [splitPosition, setSplitPosition] = useState(50); // Percentage for split view
  const splitDividerRef = useRef(null);
  const isDraggingSplitRef = useRef(false);

  // Progressive disclosure states
  const [showAdvancedControls, setShowAdvancedControls] = useState(false);
  const [compactMode, setCompactMode] = useState(false);

  // Split store subscriptions to prevent unnecessary re-renders
  const relationships = useTokenExaminerStore(state => state.relationships);
  const examinationStatus = useTokenExaminerStore(state => state.examinationStatus);
  const viewMode = useTokenExaminerStore(state => state.viewMode);
  const filters = useTokenExaminerStore(state => state.filters);
  const isLoading = useTokenExaminerStore(state => state.isLoading);
  const error = useTokenExaminerStore(state => state.error);
  const isConnected = useTokenExaminerStore(state => state.isConnected);
  const activeUsers = useTokenExaminerStore(state => state.activeUsers);

  // Actions (these don't cause re-renders)
  const selectEntity = useTokenExaminerStore(state => state.selectEntity);
  const setViewMode = useTokenExaminerStore(state => state.setViewMode);
  const setError = useTokenExaminerStore(state => state.setError);
  const connectWebSocket = useTokenExaminerStore(state => state.connectWebSocket);
  const disconnectWebSocket = useTokenExaminerStore(state => state.disconnectWebSocket);
  const setRelationships = useTokenExaminerStore(state => state.setRelationships);
  const setExaminationStatus = useTokenExaminerStore(state => state.setExaminationStatus);
  const setNetworkInsights = useTokenExaminerStore(state => state.setNetworkInsights);
  const getFilteredRelationships = useTokenExaminerStore(state => state.getFilteredRelationships);
  const getHealthStatistics = useTokenExaminerStore(state => state.getHealthStatistics);

  // Define loadEntityData before it's used in useEffect
  const loadEntityData = useCallback(async (entityId = currentEntity?.id) => {
    if (!entityId) return;

    try {
      console.log('Loading Token Examiner data for entity:', entityId);

      const status = await api.getTokenExaminationStatus(entityId);
      const relResponse = await api.getEntityRelationships(entityId, {
        includeAssessment: true,
        limit: 1000,
      });

      if (relResponse.relationships && relResponse.relationships.length > 0) {
        setExaminationStatus(status);
        setRelationships(relResponse.relationships);

        if (relResponse.summary) {
          setNetworkInsights({
            overallHealth: getHealthCategory(relResponse.summary.averageHealth),
            keyPatterns: [],
            systemicFactors: [],
            culturalAlignment: 'moderate',
            recommendedInterventions: [],
          });
        }
      } else {
        // No data available - clear state and show helpful message
        setExaminationStatus(null);
        setRelationships([]);
        setNetworkInsights(null);
        setError('No Token Examiner data available. Run 3-pass analysis on documents first.');
      }
    } catch (err) {
      console.error('Error loading entity data:', err);
      setError(`Failed to load data: ${err.message}`);
      // Clear state on error
      setExaminationStatus(null);
      setRelationships([]);
      setNetworkInsights(null);
    }
  }, [currentEntity, setExaminationStatus, setRelationships, setNetworkInsights, setError]);

  // Initialize WebSocket connection and load data when entity changes
  useEffect(() => {
    if (currentEntity && user) {
      // Set the entity in the store
      selectEntity(currentEntity.id, {
        id: currentEntity.id,
        name: currentEntity.name,
        type: currentEntity.type || 'ORGANIZATION',
        crossEntityHash: currentEntity.crossEntityHash || `CROSS_ENTITY_${currentEntity.id}`,
      });

      // Connect WebSocket
      connectWebSocket(currentEntity.id, user.uid);

      return () => {
        disconnectWebSocket();
        // Clean up any open menus when component unmounts
        setFilterAnchorEl(null);
        setSettingsAnchorEl(null);
      };
    }
  }, [currentEntity, user, selectEntity, connectWebSocket, disconnectWebSocket]);

  // Separate effect for loading data
  useEffect(() => {
    if (currentEntity?.id) {
      loadEntityData(currentEntity.id);
    }
  }, [currentEntity?.id, loadEntityData]);

  const handleAnalyzeRelationships = async () => {
    if (!currentEntity) return;

    try {
      const result = await api.analyzeEntityRelationships(currentEntity.id, {
        includeHealth: true,
        includeNetworkAnalysis: true,
      });

      if (result.status === 'success') {
        setRelationships(result.relationships);
        setNetworkInsights(result.networkInsights);
        showSnackbar('Relationship analysis completed', 'success');
      }
    } catch (err) {
      console.error('Error analyzing relationships:', err);
      showSnackbar('Failed to analyze relationships', 'error');
    }
  };

  const handleExportData = () => {
    const data = {
      entity: currentEntity,
      relationships: getFilteredRelationships(),
      statistics: getHealthStatistics(),
      exportDate: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentEntity?.name || 'entity'}_relationships_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const healthStats = getHealthStatistics();

  // Handle split view resizing
  const handleSplitDividerMouseDown = (e) => {
    isDraggingSplitRef.current = true;
    e.preventDefault();

    const handleMouseMove = (event) => {
      if (!isDraggingSplitRef.current) return;

      const container = splitDividerRef.current?.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newPosition = ((event.clientX - containerRect.left) / containerRect.width) * 100;

      // Limit split position between 20% and 80%
      setSplitPosition(Math.max(20, Math.min(80, newPosition)));
    };

    const handleMouseUp = () => {
      isDraggingSplitRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Render current view component
  const renderView = useMemo(() => () => {
    if (!currentEntity) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Select an entity to begin relationship discovery
          </Typography>
        </Box>
      );
    }

    if (viewMode === 'split') {
      return (
        <Box sx={{ display: 'flex', height: '100%', position: 'relative', userSelect: isDraggingSplitRef.current ? 'none' : 'auto' }}>
          <Box sx={{ width: `${splitPosition}%`, position: 'relative' }}>
            <ForceDirectedNetwork />
          </Box>

          {/* Resizable Divider */}
          <Box
            ref={splitDividerRef}
            sx={{
              width: 8,
              cursor: 'col-resize',
              backgroundColor: 'divider',
              position: 'relative',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: 'primary.main',
                opacity: 0.5,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 2,
                height: 40,
                backgroundColor: 'action.disabled',
              },
            }}
            onMouseDown={handleSplitDividerMouseDown}
          />

          <Box sx={{ flex: 1 }}>
            <RelationshipList />
          </Box>
        </Box>
      );
    }

    const ViewComponent = VIEW_MODES[viewMode]?.component;
    return ViewComponent ? <ViewComponent entityId={currentEntity?.id} /> : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, splitPosition]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top App Bar - Simplified */}
      <AppBar position="static" elevation={1} sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
        <Toolbar sx={{ minHeight: compactMode ? 48 : 64 }}>
          {/* Essential Controls Only */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant={compactMode ? "subtitle1" : "h6"} sx={{ mr: 2 }}>
              Relationship Intelligence
            </Typography>

            {/* Current Entity Display */}
            {currentEntity && (
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {currentEntity.name}
                </Typography>
                {!compactMode && (
                  <Typography variant="caption" color="text.secondary">
                    {currentEntity.type || 'ORGANIZATION'}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Essential Status */}
          {currentEntity && healthStats.average > 0 && (
            <Chip
              label={`Health: ${Math.round(healthStats.average)}`}
              size="small"
              sx={{
                backgroundColor: getFaanaHealthColor(healthStats.average),
                color: getContrastText(getFaanaHealthColor(healthStats.average)),
                mr: 2,
              }}
            />
          )}

          {/* Primary Actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size={compactMode ? "small" : "medium"}
              startIcon={<PsychologyIcon />}
              onClick={handleAnalyzeRelationships}
              disabled={!currentEntity || relationships.length === 0}
            >
              Analyze
            </Button>

            {/* Toggle for Advanced Controls */}
            <IconButton
              onClick={() => setShowAdvancedControls(!showAdvancedControls)}
              sx={{
                bgcolor: showAdvancedControls ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        </Toolbar>

        {/* Advanced Controls - Collapsible */}
        <Collapse in={showAdvancedControls}>
          <Box sx={{ px: 2, pb: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
              {/* Secondary Status Indicators */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton size="small" onClick={() => setDrawerOpen(true)}>
                  <Badge badgeContent={activeUsers.size} color="primary">
                    <PeopleIcon />
                  </Badge>
                </IconButton>

                {isConnected && (
                  <Chip
                    icon={<NetworkCheckIcon />}
                    label="Connected"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}

                {examinationStatus && (
                  <Tooltip title={`${examinationStatus.examined} of ${examinationStatus.total} tokens examined`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress
                        variant="determinate"
                        value={examinationStatus.percentComplete}
                        size={16}
                        thickness={4}
                      />
                      <Typography variant="caption">
                        {examinationStatus.percentComplete}%
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>

              {/* Secondary Actions */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
                  <Badge badgeContent={Object.keys(filters).length} color="primary">
                    <FilterIcon />
                  </Badge>
                </IconButton>

                <IconButton size="small" onClick={loadEntityData} disabled={!currentEntity}>
                  <RefreshIcon />
                </IconButton>

                <IconButton size="small" onClick={handleExportData} disabled={!currentEntity}>
                  <DownloadIcon />
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => setCompactMode(!compactMode)}
                  title={compactMode ? "Expand interface" : "Compact interface"}
                >
                  <ViewColumnIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        {/* View Mode Tabs */}
        <Tabs
          value={viewMode}
          onChange={(e, newMode) => setViewMode(newMode)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {Object.entries(VIEW_MODES).map(([mode, config]) => (
            <Tab
              key={mode}
              value={mode}
              label={config.label}
              icon={config.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </AppBar>

      {/* Progress Bar */}
      {isLoading && <LinearProgress />}

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Examination Progress - Collapsible when compact */}
        {currentEntity && !compactMode && <ExaminationProgress />}

        {/* Network Insights Panel - Collapsible when compact */}
        {currentEntity && relationships.length > 0 && !compactMode && <NetworkInsightsPanel />}

        {/* Main Visualization Area */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {renderView()}
        </Box>
      </Box>

      {/* Batch Actions Bar */}
      {selectedRelationshipIds.length > 0 && (
        <BatchActionsBar
          selectedRelationships={selectedRelationshipIds}
          onSelectionChange={setSelectedRelationshipIds}
        />
      )}

      {/* Filter Menu */}
      {filterAnchorEl && (
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => setFilterAnchorEl(null)}
          MenuListProps={{
            disablePortal: true,
          }}
        >
          <Box sx={{ width: 400, p: 2 }}>
            <FilterPanel />
          </Box>
        </Menu>
      )}

      {/* Settings Menu */}
      {settingsAnchorEl && (
        <Menu
          anchorEl={settingsAnchorEl}
          open={Boolean(settingsAnchorEl)}
          onClose={() => setSettingsAnchorEl(null)}
          MenuListProps={{
            disablePortal: true,
          }}
        >
          <MenuItem>Auto-refresh: Off</MenuItem>
          <MenuItem>Confidence threshold: Medium</MenuItem>
          <MenuItem>Show unconfirmed: Yes</MenuItem>
        </Menu>
      )}

      {/* Left Drawer - Active Users */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 300, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Active Users
          </Typography>
          <List>
            {Array.from(activeUsers.entries()).map(([userId, userData]) => (
              <ListItem key={userId}>
                <ListItemIcon>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText
                  primary={userData.name}
                  secondary={`${userData.role || 'Viewer'} • Online`}
                />
              </ListItem>
            ))}
            {activeUsers.size === 0 && (
              <ListItem>
                <ListItemText
                  primary="No other users online"
                  secondary="You are the only active user"
                />
              </ListItem>
            )}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            Real-time collaboration enabled. Changes are synchronized across all active sessions.
          </Typography>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}

export default React.memo(TokenExaminer);